import React, { useMemo, useState } from 'react';
import { ExternalLink, Search, X } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL, GeoCell } from '../shared';
import { fmtDateTime, fmtEur, fmtNum, parseDate } from '../../../lib/fmt';
import DataTable from '../../../components/admin/DataTable';
import BanDialog from '../../../components/admin/BanDialog';

// Minúsculas y sin tildes, para buscar «Ñandú» escribiendo «nandu»
const norm = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const ROLE_FILTERS = [['', 'Todos los roles'], ['user', 'Jugadores'], ['admin', 'Admins']];
const STATE_FILTERS = [['', 'Todos'], ['online', 'Conectados'], ['banned', 'Baneados']];

export default function UsuariosTab({ users, fetchUsers, showFeedback, onViewStats }) {
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [userCharsCache, setUserCharsCache] = useState({});
  const [charsLoading, setCharsLoading]     = useState(null);
  const [banModal, setBanModal]   = useState(null); // { username, isBanned, banReason }

  // Filtros
  const [q, setQ]               = useState('');
  const [role, setRole]         = useState('');
  const [state, setState]       = useState('');
  const [withBuys, setWithBuys] = useState(false);
  const [withBalance, setWithBalance] = useState(false);

  const loadUserChars = async (userId) => {
    if (userCharsCache[userId] !== undefined) {
      setExpandedUserId(prev => prev === userId ? null : userId);
      return;
    }
    setCharsLoading(userId);
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/users/${userId}/characters`,
        { withCredentials: true }
      );
      setUserCharsCache(prev => ({ ...prev, [userId]: data }));
      setExpandedUserId(userId);
    } catch {
      setUserCharsCache(prev => ({ ...prev, [userId]: [] }));
      setExpandedUserId(userId);
    } finally {
      setCharsLoading(null);
    }
  };

  const term = norm(q.trim());
  const filtered = useMemo(() => users.flatMap((u) => {
    if (role && u.role !== role) return [];
    if (state === 'online' && !u.is_online) return [];
    if (state === 'banned' && !u.is_banned) return [];
    if (withBuys && !(u.purchases > 0 || u.topups > 0)) return [];
    if (withBalance && !(u.frost_crystals > 0)) return [];
    if (!term) return [{ ...u, _charMatch: null }];
    const direct = [u.username, u.email, u.game_account].some((v) => norm(v).includes(term));
    const charMatch = (u.characters || []).find((c) => norm(c).includes(term));
    if (!direct && !charMatch) return [];
    return [{ ...u, _charMatch: direct ? null : charMatch }];
  }), [users, term, role, state, withBuys, withBalance]);

  const anyFilter = q || role || state || withBuys || withBalance;
  const clearFilters = () => { setQ(''); setRole(''); setState(''); setWithBuys(false); setWithBalance(false); };

  const columns = [
    {
      key: 'username', label: 'Usuario', width: 190, hideable: false, csv: (u) => u.username,
      render: (u) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, maxWidth: '100%' }}>
          {u.is_online && <span title="Conectado" style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', flexShrink: 0, boxShadow: '0 0 6px #4ade80' }} />}
          <button type="button" className="adm-link" title="Abrir ficha de la cuenta"
            style={{ color: u.role === 'admin' ? '#FFD700' : '#00e5ff', overflow: 'hidden', textOverflow: 'ellipsis' }}
            onClick={() => onViewStats && onViewStats(u.username)}>
            {u.username}
          </button>
          {u._charMatch && <span className="adm-chip" title="Coincide por personaje">PJ: {u._charMatch}</span>}
        </span>
      ),
    },
    { key: 'email', label: 'Correo', width: 220, title: (u) => u.email, render: (u) => <span className="adm-mut">{u.email}</span> },
    { key: 'game_account', label: 'Cuenta de juego', width: 150, sortValue: (u) => u.game_account || u.username?.toUpperCase(),
      csv: (u) => u.game_account || u.username?.toUpperCase(),
      render: (u) => <span className="adm-mono adm-mut">{u.game_account || u.username?.toUpperCase()}</span> },
    { key: 'role', label: 'Rol', width: 90,
      render: (u) => <span className={`user-role-badge ${u.role === 'admin' ? 'user-role-badge--admin' : ''}`}>{u.role}</span> },
    { key: 'frost_crystals', label: 'Cristales', width: 110, align: 'right', sortValue: (u) => u.frost_crystals ?? 0,
      render: (u) => <><b style={{ color: '#00e5ff' }}>{fmtNum(u.frost_crystals ?? 0)}</b> <span className="adm-mut">❄</span></> },
    { key: 'paid_eur', label: 'Pagado', width: 110, align: 'right', sortValue: (u) => u.paid_eur || null,
      csv: (u) => String(u.paid_eur || 0).replace('.', ','),
      title: (u) => (u.topups ? `${u.topups} ${u.topups === 1 ? 'recarga' : 'recargas'}` : undefined),
      render: (u) => (u.paid_eur ? <b style={{ color: '#fcd34d' }}>{fmtEur(u.paid_eur)}</b> : <span className="adm-mut">—</span>) },
    { key: 'purchases', label: 'Compras', width: 95, align: 'right', sortValue: (u) => u.purchases || null,
      render: (u) => (u.purchases ? fmtNum(u.purchases) : <span className="adm-mut">—</span>) },
    { key: 'char_count', label: 'PJs', width: 70, align: 'right', title: (u) => (u.characters || []).join(', '),
      csv: (u) => (u.characters || []).join(', '), render: (u) => u.char_count || <span className="adm-mut">0</span> },
    { key: 'last_ip', label: 'IP', width: 135, title: (u) => u.last_ip,
      render: (u) => (u.last_ip && u.last_ip !== '0.0.0.0' ? <span className="adm-mono adm-mut">{u.last_ip}</span> : <span className="adm-mut">—</span>) },
    { key: 'geo', label: 'Ubicación', width: 150, sortable: false, csv: false, render: (u) => <GeoCell ip={u.last_ip} /> },
    { key: 'created_at', label: 'Registro', width: 150, sortValue: (u) => parseDate(u.created_at)?.getTime(),
      csv: (u) => fmtDateTime(u.created_at, ''), render: (u) => <span className="adm-mut">{fmtDateTime(u.created_at)}</span> },
    { key: 'last_login', label: 'Últ. conexión', width: 150, sortValue: (u) => parseDate(u.last_login)?.getTime(),
      csv: (u) => fmtDateTime(u.last_login, ''), render: (u) => <span className="adm-mut">{fmtDateTime(u.last_login)}</span> },
    {
      key: 'acciones', label: 'Acciones', width: 190, sortable: false, hideable: false, csv: false,
      render: (u) => (
        <span style={{ display: 'inline-flex', gap: 6 }}>
          <button type="button" onClick={() => loadUserChars(u._id)}
            style={{ background: 'none', border: '1px solid rgba(0,229,255,0.3)', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', color: '#00e5ff', fontSize: 11, fontWeight: 700 }}>
            {charsLoading === u._id ? '...' : expandedUserId === u._id ? '▲ PJs' : '▼ PJs'}
          </button>
          <button type="button"
            onClick={() => setBanModal({ username: u.username, isBanned: !!u.is_banned, banReason: u.ban_reason })}
            title={u.is_banned ? `Baneado: ${u.ban_reason || '—'}` : 'Banear cuenta'}
            style={{
              background: u.is_banned ? 'rgba(52,211,153,0.1)' : 'rgba(248,113,113,0.08)',
              border: `1px solid ${u.is_banned ? 'rgba(52,211,153,0.35)' : 'rgba(248,113,113,0.25)'}`,
              borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
              color: u.is_banned ? '#34d399' : '#f87171', fontSize: 11, fontWeight: 700,
            }}>
            {u.is_banned ? 'DESBANEAR' : 'BANEAR'}
          </button>
        </span>
      ),
    },
  ];

  const renderExpanded = (u) => {
    const chars = userCharsCache[u._id] || [];
    if (!chars.length) return <span className="adm-mut" style={{ fontSize: 12 }}>Sin personajes</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 18px', fontSize: 12 }}>
        {chars.map((c) => (
          <a key={c.name} href={`/armeria/${encodeURIComponent(c.name)}`} target="_blank" rel="noopener noreferrer"
            title="Abrir en la armería" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{ color: c.class_color, fontWeight: 700 }}>{c.name}</span>
            <span className="adm-mut">Nv.{c.level} {c.class_name}</span>
            {c.online && <span style={{ color: '#4ade80' }}>●</span>}
            <ExternalLink size={11} style={{ color: 'rgba(224,247,255,0.35)' }} />
          </a>
        ))}
      </div>
    );
  };

  const toolbar = (
    <>
      <div style={{ position: 'relative' }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'rgba(0,229,255,0.5)', pointerEvents: 'none' }} />
        <input
          className="adm-input" style={{ paddingLeft: 30, paddingRight: 28, width: 'min(300px, 70vw)' }}
          placeholder="Cuenta, email o personaje…" value={q} onChange={(e) => setQ(e.target.value)}
          autoFocus data-testid="users-search" aria-label="Buscar usuarios"
        />
        {q && (
          <button type="button" onClick={() => setQ('')} aria-label="Borrar búsqueda"
            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 0, color: 'rgba(224,247,255,0.5)', cursor: 'pointer', display: 'flex' }}>
            <X size={14} />
          </button>
        )}
      </div>
      <select className="adm-select" value={role} onChange={(e) => setRole(e.target.value)} aria-label="Rol">
        {ROLE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <select className="adm-select" value={state} onChange={(e) => setState(e.target.value)} aria-label="Estado" data-testid="users-state">
        {STATE_FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
      <label className="adm-check"><input type="checkbox" checked={withBuys} onChange={(e) => setWithBuys(e.target.checked)} /> Con compras o recargas</label>
      <label className="adm-check"><input type="checkbox" checked={withBalance} onChange={(e) => setWithBalance(e.target.checked)} /> Con saldo</label>
      {anyFilter && <button type="button" className="adm-btn adm-btn--ghost" onClick={clearFilters}><X size={13} /> Limpiar</button>}
    </>
  );

  return (
    <>
    <div className="admin-section anim-fade-up" style={{ animationDelay: '0.15s' }}>
      <div className="admin-section-header" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        <h2 className="admin-section-title">
          Usuarios registrados ({anyFilter ? `${fmtNum(filtered.length)} de ${fmtNum(users.length)}` : fmtNum(users.length)})
        </h2>
      </div>
      <DataTable
        testId="users-table"
        storageKey="fb-admin-users-table-v1"
        columns={columns}
        rows={filtered}
        rowKey={(u) => u._id}
        defaultSort={{ key: 'created_at', dir: 'desc' }}
        expandedKey={expandedUserId}
        renderExpanded={renderExpanded}
        toolbar={toolbar}
        csvName="usuarios"
        emptyText={anyFilter ? 'Ningún usuario coincide con los filtros.' : 'No hay usuarios.'}
      />
    </div>
      {banModal && (
        <BanDialog
          banModal={banModal}
          showFeedback={showFeedback}
          onClose={() => setBanModal(null)}
          onDone={() => { setBanModal(null); fetchUsers(); }}
        />
      )}
    </>
  );
}
